import {useEffect, useMemo, useState} from "react";
import {getAuth, onAuthStateChanged} from "firebase/auth";
import {doc, getDoc, onSnapshot} from "firebase/firestore";
import {httpsCallable} from "firebase/functions";
import {db, functions} from "../Security/firebase-config";
import {FUNCTION_NAMES} from "../utils/functionNames";
import {TEAM_QUOTA_REFRESH_EVENT,reconcileTeamQuota} from "../utils/teamQuota";
import {ensureTeamQuotaAccounting} from "../utils/teamQuotaRepair";

function asNumber(value, fallback = 0) {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

function buildUploadBlockMessage({teamQuota, userQuota}) {
  if (!teamQuota) return null;

  const effectiveStorageUsedBytes =
    asNumber(teamQuota?.storageUsedBytes, 0) +
    asNumber(teamQuota?.liveReservedStorageBytes, 0);
  const effectiveMonthlyUploadUsedBytes =
    asNumber(teamQuota?.monthlyUploadUsedBytes, 0) +
    asNumber(teamQuota?.liveReservedUploadBytes, 0);

  if (teamQuota?.hasActiveEntitlement !== true) {
    return "This team does not currently have an active plan that allows uploads.";
  }

  if (
    asNumber(teamQuota?.storageLimitBytes, 0) > 0 &&
    effectiveStorageUsedBytes >= asNumber(teamQuota?.storageLimitBytes, 0)
  ) {
    return "Your team storage allowance is full.";
  }

  if (
    teamQuota?.hasHardUploadCap === true &&
    asNumber(teamQuota?.monthlyUploadLimitBytes, 0) > 0 &&
    effectiveMonthlyUploadUsedBytes >= asNumber(teamQuota?.monthlyUploadLimitBytes, 0)
  ) {
    return "Your team has reached its upload allowance for this billing cycle.";
  }

  if (
    asNumber(userQuota?.egressLimitBytes, 0) > 0 &&
    asNumber(userQuota?.egressUsedBytes, 0) >= asNumber(userQuota?.egressLimitBytes, 0)
  ) {
    return "Your personal egress allowance for this billing cycle has been reached.";
  }

  return null;
}

export default function useTeamQuotaStatus(teamId) {
  const [uid, setUid] = useState(() => getAuth().currentUser?.uid || null);
  const [loading, setLoading] = useState(Boolean(teamId));
  const [teamQuota, setTeamQuota] = useState(null);
  const [userQuota, setUserQuota] = useState(null);
  const [fallbackQuota, setFallbackQuota] = useState(null);
  const [fallbackError, setFallbackError] = useState("");

  useEffect(() => {
    const stop = onAuthStateChanged(getAuth(), (user) => {
      setUid(user?.uid || null);
    });

    return () => stop();
  }, []);

  useEffect(() => {
    if (!teamId) {
      setTeamQuota(null);
      setUserQuota(null);
      setFallbackQuota(null);
      setFallbackError("");
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    setTeamQuota(null);setUserQuota(null);
    setFallbackQuota(null);
    setFallbackError("");
    let cancelled = false;
    let version = 0, userVersion = 0;
    const receiveTeam = snapshot => {
      if(cancelled)return;
      const data=snapshot.exists()?snapshot.data()||null:null;
      setTeamQuota(data);setLoading(false);
      if(data&&uid)void ensureTeamQuotaAccounting({teamId,uid,version:data.quotaAccountingVersion,repair:reconcileTeamQuota})
        .catch(error=>console.warn('Could not reconcile team usage:',error));
    };

    const loadQuotaDocs = async () => {
      const request=++version, userRequest=userVersion;
      try {
        const currentRef = doc(db, "teams", teamId, "quotaV2", "current");
        const [teamSnap, userSnap] = await Promise.all([
          getDoc(currentRef),
          uid ?
            getDoc(doc(db, "teams", teamId, "quotaV2", "current", "userEgress", uid)) :
            Promise.resolve(null),
        ]);

        if (cancelled || request!==version) return;

        receiveTeam(teamSnap);
        if(userRequest===userVersion)setUserQuota(userSnap?.exists?.() ? userSnap.data() || null : null);
      } catch (error) {
        if (cancelled || request!==version) return;
        setUserQuota(null);
      } finally {
        if (!cancelled && request===version) {
          setLoading(false);
        }
      }
    };

    const handleVisibilityRefresh = () => {
      if (document.visibilityState === "visible") {
        loadQuotaDocs();
      }
    };

    const handleQuotaRefresh = (event) => {
      const eventTeamId = event?.detail?.teamId || null;
      if (!eventTeamId || eventTeamId === teamId) {
        loadQuotaDocs();
      }
    };

    loadQuotaDocs();
    const stopTeam=onSnapshot(doc(db,'teams',teamId,'quotaV2','current'),snapshot=>{version++;receiveTeam(snapshot);},()=>{});
    const stopUser=uid?onSnapshot(doc(db,'teams',teamId,'quotaV2','current','userEgress',uid),snapshot=>{if(!cancelled){userVersion++;setUserQuota(snapshot.exists()?snapshot.data()||null:null);}},()=>{}):()=>{};
    window.addEventListener("focus", loadQuotaDocs);
    document.addEventListener("visibilitychange", handleVisibilityRefresh);
    window.addEventListener(TEAM_QUOTA_REFRESH_EVENT, handleQuotaRefresh);

    return () => {
      cancelled = true;
      stopTeam();stopUser();
      window.removeEventListener("focus", loadQuotaDocs);
      document.removeEventListener("visibilitychange", handleVisibilityRefresh);
      window.removeEventListener(TEAM_QUOTA_REFRESH_EVENT, handleQuotaRefresh);
    };
  }, [teamId, uid]);

  useEffect(() => {
    if (!teamId || !uid || loading) return undefined;
    if (!FUNCTION_NAMES.checkTeamQuotaAllowance) return undefined;

    const hasLimitData =
      asNumber(teamQuota?.storageLimitBytes, 0) > 0 ||
      asNumber(teamQuota?.monthlyUploadLimitBytes, 0) > 0;

    if (fallbackQuota) return undefined;
    if (teamQuota && hasLimitData) return undefined;

    let cancelled = false;

    async function repairQuotaSnapshot() {
      try {
        const checkQuota = httpsCallable(
          functions,
          FUNCTION_NAMES.checkTeamQuotaAllowance
        );
        const result = await checkQuota({
          teamId,
          action: "upload_video",
          bytesToAdd: 0,
        });

        if (cancelled) return;

        const quota = result?.data?.quota || null;
        if (quota) {
          setFallbackQuota(quota);
          setFallbackError("");
        }
      } catch (error) {
        if (cancelled) return;
        console.warn("Quota fallback load failed:", error);
        setFallbackError(
          error?.message || "Unable to refresh quota allowances."
        );
      }
    }

    repairQuotaSnapshot();

    return () => {
      cancelled = true;
    };
  }, [fallbackQuota, loading, teamId, teamQuota, uid]);

  return useMemo(() => {
    const resolvedTeamQuota = {
      ...(fallbackQuota || {}),
      ...(teamQuota || {}),
      storageLimitBytes:
        typeof teamQuota?.storageLimitBytes === "number" && Number.isFinite(teamQuota.storageLimitBytes) ?
          teamQuota.storageLimitBytes :
          fallbackQuota?.storageLimitBytes,
      liveReservedStorageBytes:
        typeof teamQuota?.liveReservedStorageBytes === "number" && Number.isFinite(teamQuota.liveReservedStorageBytes) ?
          teamQuota.liveReservedStorageBytes :
          fallbackQuota?.liveReservedStorageBytes,
      monthlyUploadLimitBytes:
        typeof teamQuota?.monthlyUploadLimitBytes === "number" && Number.isFinite(teamQuota.monthlyUploadLimitBytes) ?
          teamQuota.monthlyUploadLimitBytes :
          fallbackQuota?.monthlyUploadLimitBytes,
      liveReservedUploadBytes:
        typeof teamQuota?.liveReservedUploadBytes === "number" && Number.isFinite(teamQuota.liveReservedUploadBytes) ?
          teamQuota.liveReservedUploadBytes :
          fallbackQuota?.liveReservedUploadBytes,
      proIncludedUploadBytes:
        typeof teamQuota?.proIncludedUploadBytes === "number" && Number.isFinite(teamQuota.proIncludedUploadBytes) ?
          teamQuota.proIncludedUploadBytes :
          fallbackQuota?.proIncludedUploadBytes,
      hasActiveEntitlement:
        typeof teamQuota?.hasActiveEntitlement === "boolean" ?
          teamQuota.hasActiveEntitlement :
          fallbackQuota?.hasActiveEntitlement,
      hasHardUploadCap:
        typeof teamQuota?.hasHardUploadCap === "boolean" ?
          teamQuota.hasHardUploadCap :
          fallbackQuota?.hasHardUploadCap,
    };
    const hasQuotaDoc = !!teamQuota || !!fallbackQuota;
    const hasActiveEntitlement = hasQuotaDoc ? resolvedTeamQuota?.hasActiveEntitlement === true : true;
    const effectiveStorageUsedBytes =
      asNumber(resolvedTeamQuota?.storageUsedBytes, 0) +
      asNumber(resolvedTeamQuota?.liveReservedStorageBytes, 0);
    const effectiveMonthlyUploadUsedBytes =
      asNumber(resolvedTeamQuota?.monthlyUploadUsedBytes, 0) +
      asNumber(resolvedTeamQuota?.liveReservedUploadBytes, 0);
    const storageExceeded =
      asNumber(resolvedTeamQuota?.storageLimitBytes, 0) > 0 &&
      effectiveStorageUsedBytes >= asNumber(resolvedTeamQuota?.storageLimitBytes, 0);
    const monthlyUploadExceeded =
      resolvedTeamQuota?.hasHardUploadCap === true &&
      asNumber(resolvedTeamQuota?.monthlyUploadLimitBytes, 0) > 0 &&
      effectiveMonthlyUploadUsedBytes >= asNumber(resolvedTeamQuota?.monthlyUploadLimitBytes, 0);
    const userEgressExceeded =
      asNumber(userQuota?.egressLimitBytes, 0) > 0 &&
      asNumber(userQuota?.egressUsedBytes, 0) >= asNumber(userQuota?.egressLimitBytes, 0);
    const uploadBlocked =
      !hasActiveEntitlement ||
      storageExceeded ||
      monthlyUploadExceeded ||
      userEgressExceeded;
    const canWatchVod = !userEgressExceeded && hasActiveEntitlement;

    return {
      loading,
      uid,
      teamQuota: hasQuotaDoc ? resolvedTeamQuota : null,
      userQuota,
      fallbackError,
      storageExceeded,
      monthlyUploadExceeded,
      userEgressExceeded,
      effectiveStorageUsedBytes,
      effectiveMonthlyUploadUsedBytes,
      uploadBlocked,
      canWatchVod,
      uploadBlockMessage: buildUploadBlockMessage({
        teamQuota: hasQuotaDoc ? resolvedTeamQuota : null,
        userQuota,
      }),
    };
  }, [fallbackError, fallbackQuota, loading, teamQuota, uid, userQuota]);
}
